'use client'

import { ROUTES } from '@/core/constants/routes'
import { capitalizeFirstLetter } from '@/core/lib/utils/capitalize-first-letter'
import { cn } from '@/core/lib/utils/cn'
import { NavLink } from '@/core/ui/atoms/nav-link'
import { Link } from '@/i18n/navigation'
import { Accordion, AccordionItem } from '@heroui/accordion'
import { ChevronRight, FolderOpen } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import { useEffect } from 'react'
import { HeadlineModel } from '../../../types/models/headline.model'
import { useBlogSidebarStore } from './store'

type HeadlineListProps = {
  headlines: HeadlineModel[]
}

export const HeadlineList: React.FC<HeadlineListProps> = ({ headlines }) => {
  const t = useTranslations()
  const params = useParams()
  const headline = params?.headline?.toString() || ''
  const { activeHeadline, setActiveHeadline } = useBlogSidebarStore()

  useEffect(() => {
    setActiveHeadline(new Set([headline]))
  }, [headline, setActiveHeadline])

  const isSelected = (key: string) => {
    if (activeHeadline === 'all') return true
    if (activeHeadline instanceof Set) return activeHeadline.has(key)
    return false
  }

  if (!headlines.length) {
    return (
      <div className='flex flex-col items-center gap-3 p-3 text-gray-500'>
        <p>{t('blog.noHeadlines')}</p>
      </div>
    )
  }

  return (
    <>
      <Accordion
        selectedKeys={activeHeadline}
        onSelectionChange={setActiveHeadline}
        showDivider={false}
        className='space-y-2'
        itemClasses={{
          trigger: 'py-1.5 px-2',
        }}
      >
        {headlines.map(h => (
          <AccordionItem
            key={h.slug}
            indicator={({ isOpen }) => (
              <ChevronRight
                className={cn(
                  'text-gray-400 transition-transform duration-300',
                  isOpen && 'text-primary rotate-90',
                )}
              />
            )}
            title={
              <Link
                onClick={e => e.stopPropagation()}
                href={ROUTES.headline(h.slug)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-1 py-1 font-semibold transition-colors duration-200',
                  isSelected(h.slug)
                    ? 'text-creamed-cron'
                    : 'hover:text-creamed-cron text-gray-800',
                )}
              >
                <FolderOpen
                  size={16}
                  className={cn(
                    isSelected(h.slug) ? 'text-creamed-cron' : 'text-gray-400',
                  )}
                />
                {capitalizeFirstLetter(h.name)}
              </Link>
            }
          >
            <ul className='mt-1 flex list-none flex-col gap-1 pl-6'>
              {h.posts.map(post => (
                <li key={post.id}>
                  <NavLink
                    href={ROUTES.post(post.slug)}
                    className={{
                      main: 'hover:text-creamed-cron block py-1 text-sm text-gray-600 transition-colors duration-150',
                    }}
                  >
                    {capitalizeFirstLetter(post.name)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  )
}
